import {
  AlertTriangle,
  ArrowLeft,
  Clock,
  Mail,
  MessageSquare,
  Phone,
  Wrench,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import AppNavbar from "@/components/common/AppNavbar";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { authService } from "@/services/authService";
import { ContactInfo, ContactUsService } from "@/services/landing/contactUsService";
import SubscriptionService from "@/services/landing/subscriptionService";
import { RootState } from "@/store";

interface MaintenanceProps {
  estimatedTime?: string;
  message?: string;
  service?: string;
  showEmail?: boolean;
}

export default function Maintenance({
  estimatedTime = "2-4 hours",
  message = "We're performing scheduled maintenance to improve your experience",
  service = "Service",
  showEmail = true,
}: MaintenanceProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [isNotified, setIsNotified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [animate, setAnimate] = useState(false);
  const [contactInfo, setContactInfo] = useState<ContactInfo | null>(null);
  const [contactLoading, setContactLoading] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState<null | string>(null);

  // Maintenance progress animation
  const [progress, setProgress] = useState(0);

  // Parse optional query params to make this page reusable across routes
  const params = new URLSearchParams(location.search);
  const affectedRoute =
    params.get("route") || params.get("affected") || params.get("path") || "";
  const estimatedFromQuery = params.get("estimatedTime") || estimatedTime;
  const messageFromQuery = params.get("message") || message;
  const serviceFromQuery = params.get("service") || service;
  const redirectAfter = Number(params.get("redirectAfter") || params.get("returnAfter") || 0);
  const redirectTo = params.get("returnTo") || params.get("redirectTo") || "/";
  const [countdown, setCountdown] = useState<number>(redirectAfter);

  useEffect(() => {
    if (redirectAfter && redirectAfter > 0) {
      setCountdown(redirectAfter);
    }
    setAnimate(true);

    // Simulate maintenance progress
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev < 85) return prev + Math.random() * 15;
        return prev;
      });
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const loadContact = async () => {
      try {
        setContactLoading(true);
        const info = await ContactUsService.get();
        setContactInfo(info);
      } catch (err) {
        console.error("Failed to load contact info:", err);
      } finally {
        setContactLoading(false);
      }
    };
    loadContact();
  }, []);

  // Handle auto-redirect countdown when redirectAfter is provided
  useEffect(() => {
    if (!redirectAfter || redirectAfter <= 0) return;
    if (countdown <= 0) {
      navigate(redirectTo);
      return;
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown, redirectAfter, redirectTo, navigate]);

  useEffect(() => {
    // Add custom CSS animations to document head
    const style = document.createElement("style");
    style.textContent = `
      @keyframes wrench-rotate {
        0%, 100% {
          transform: rotate(-15deg);
        }
        50% {
          transform: rotate(15deg);
        }
      }

      @keyframes pulse-ring {
        0% {
          transform: scale(0.95);
          opacity: 1;
        }
        100% {
          transform: scale(1.3);
          opacity: 0;
        }
      }

      @keyframes slide-in-down {
        from {
          opacity: 0;
          transform: translateY(-20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      @keyframes slide-in-up {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      @keyframes fade-in {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }

      .maintenance-icon {
        animation: wrench-rotate 2s ease-in-out infinite;
      }

      .pulse-ring {
        animation: pulse-ring 2s ease-out infinite;
      }

      .slide-in-down {
        animation: slide-in-down 0.6s ease-out;
      }

      .slide-in-up {
        animation: slide-in-up 0.6s ease-out;
      }

      .fade-in {
        animation: fade-in 0.6s ease-out;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const handleGoBack = async () => {
    if (isAuthenticated && user) {
      // User is logged in, logout first then go home
      try {
        await authService.logout();
      } catch (err) {
        console.error("Logout error:", err);
      }
      navigate("/", { replace: true });
    } else {
      // No session, just go home
      navigate("/", { replace: true });
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    setSubscriptionError(null);

    try {
      const payload = { email, name: name || undefined, phone: phone || undefined };
      const res = await SubscriptionService.subscribe(payload);
      if (res && res.success === false) {
        setSubscriptionError(res.message || "Failed to subscribe");
      } else {
        setIsNotified(true);
        setEmail("");
        setName("");
        setPhone("");

        // Reset notification after 5 seconds
        setTimeout(() => setIsNotified(false), 5000);
      }
    } catch (err: any) {
      console.error("Subscription error:", err);
      // Try to parse validation errors from the API
      const resp = err?.response?.data;
      let friendly = "Failed to subscribe";
      if (resp) {
        if (resp.errors) {
          const parts: string[] = [];
          const errs = resp.errors;
          if (Array.isArray(errs)) {
            parts.push(...errs.map((e) => String(e)));
          } else if (typeof errs === "object") {
            for (const k of Object.keys(errs)) {
              const v = (errs as any)[k];
              if (Array.isArray(v)) {
                parts.push(...v.map((s) => String(s)));
              } else {
                parts.push(String(v));
              }
            }
          } else {
            parts.push(String(errs));
          }
          if (parts.length > 0) {
            friendly = parts.join("; ");
          }
        } else if (resp.message) {
          friendly = String(resp.message);
        } else {
          friendly = JSON.stringify(resp);
        }
      } else if (err?.message) {
        friendly = String(err.message);
      }

      // Friendly special-case for duplicate email wording
      if (/already (been )?taken|already subscribed|duplicate/i.test(friendly)) {
        friendly = "This email is already subscribed.";
      }

      setSubscriptionError(friendly);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <AppNavbar />
      <div className="flex min-h-screen flex-col bg-gradient-to-br from-base-200 via-base-100 to-base-200 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
        {/* Header */}
        <Breadcrumbs />
        <div className="p-6">
          <button
            className="btn btn-ghost btn-sm gap-2 hover:bg-base-300 dark:hover:bg-slate-700"
            onClick={handleGoBack}
          >
            <ArrowLeft size={16} />
            Go Back
          </button>
        </div>

        {/* Main Content */}
        <div className="flex flex-1 items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
          <div className="mx-auto w-full max-w-2xl">
            {/* Animated Icon Section */}
            <div className="slide-in-down mb-8 text-center lg:mb-12">
              <div className="relative mb-8 inline-block">
                {/* Pulse rings background */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="pulse-ring h-32 w-32 rounded-full bg-warning/20"></div>
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div
                    className="pulse-ring h-32 w-32 rounded-full bg-warning/20"
                    style={{ animationDelay: "0.5s" }}
                  ></div>
                </div>

                {/* Main icon */}
                <div className="relative z-10 flex h-32 w-32 items-center justify-center rounded-full bg-gradient-to-br from-warning/30 to-warning/10 shadow-lg">
                  <Wrench
                    className="maintenance-icon text-warning"
                    size={64}
                    strokeWidth={1.5}
                  />
                </div>
              </div>

              {/* Title */}
              <h1 className="slide-in-down mb-3 text-3xl font-bold text-base-content dark:text-white sm:text-4xl lg:text-5xl">
                Under Maintenance
              </h1>

              {/* Service name */}
              <p className="dark:text-primary-light mb-4 text-lg font-semibold text-primary">
                {serviceFromQuery}
              </p>

              {/* Description */}
              <p className="mb-4 text-base text-base-content/70 dark:text-slate-300 sm:text-lg">
                {messageFromQuery}
              </p>

              {affectedRoute && (
                <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                  Affected route: <span className="font-mono">{affectedRoute}</span>
                </p>
              )}

              {redirectAfter > 0 && (
                <p className="mb-6 text-sm text-gray-700 dark:text-gray-300">
                  Redirecting in <strong>{countdown}s</strong> to <span className="font-mono">{redirectTo}</span>
                </p>
              )}
            </div>

            {/* Status Cards Grid */}
            <div className="slide-in-up mb-8 grid gap-4 sm:grid-cols-2 lg:mb-12">
              {/* Status Card */}
              <div className="card bg-base-100 shadow-md transition-all duration-300 hover:shadow-lg dark:bg-slate-800">
                <div className="card-body">
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-info/20">
                      <AlertTriangle className="text-info" size={24} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-base-content/60 dark:text-slate-400">
                        Status
                      </p>
                      <p className="text-lg font-bold text-base-content dark:text-white">
                        Active
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* ETA Card */}
              <div className="card bg-base-100 shadow-md transition-all duration-300 hover:shadow-lg dark:bg-slate-800">
                <div className="card-body">
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-success/20">
                      <Clock className="text-success" size={24} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-base-content/60 dark:text-slate-400">
                        Est. Time
                      </p>
                      <p>Soon to be available</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="slide-in-up mb-8 lg:mb-12">
              <div className="mb-4 flex items-center justify-between">
                <p className="font-semibold text-base-content dark:text-white">
                  Maintenance Progress
                </p>
                <p className="dark:text-primary-light text-sm font-bold text-primary">
                  {Math.round(progress)}%
                </p>
              </div>
              <div className="h-3 overflow-hidden rounded-full bg-base-300 dark:bg-slate-700">
                <div
                  className="h-full bg-gradient-to-r from-primary via-success to-primary transition-all duration-500 ease-out"
                  style={{ width: `${Math.min(progress, 100)}%` }}
                ></div>
              </div>
            </div>

            {/* Notification Section */}
            {showEmail && (
              <div className="slide-in-up mb-8 lg:mb-12">
                <div className="rounded-xl bg-base-100 p-6 shadow-md dark:bg-slate-800 sm:p-8">
                  <h3 className="mb-3 flex items-center gap-2 text-lg font-bold text-base-content dark:text-white">
                    <Mail size={20} />
                    Get Notified When We're Back
                  </h3>
                  <p className="mb-6 text-sm text-base-content/70 dark:text-slate-400">
                    We'll send you an email when {service} is back online
                  </p>

                  <form
                    className="flex flex-col gap-3"
                    onSubmit={handleEmailSubmit}
                  >
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                      <input
                        className="input input-bordered col-span-1 w-full dark:border-slate-600 dark:bg-slate-700 dark:text-white dark:placeholder-slate-400"
                        disabled={isLoading}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Your name (optional)"
                        type="text"
                        value={name}
                      />
                      <input
                        className="input input-bordered col-span-1 w-full dark:border-slate-600 dark:bg-slate-700 dark:text-white dark:placeholder-slate-400"
                        disabled={isLoading}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="Phone (optional)"
                        type="tel"
                        value={phone}
                      />
                      <input
                        className="input input-bordered col-span-1 w-full dark:border-slate-600 dark:bg-slate-700 dark:text-white dark:placeholder-slate-400"
                        disabled={isLoading}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email"
                        required
                        type="email"
                        value={email}
                      />
                    </div>

                    <div className="mt-2 flex items-center gap-3">
                      <button
                        className="btn btn-primary w-full transition-all duration-300 sm:w-auto"
                        disabled={isLoading}
                        type="submit"
                      >
                        {isLoading ? (
                          <span className="loading loading-spinner loading-sm"></span>
                        ) : (
                          "Notify Me"
                        )}
                      </button>
                    </div>
                  </form>

                  {subscriptionError && (
                    <div className="mt-4 alert alert-error">
                      <span>{subscriptionError}</span>
                    </div>
                  )}

                  {isNotified && (
                    <div className="mt-4 flex items-center gap-2 rounded-lg bg-success/10 p-3 text-success">
                      <span>✓</span>
                      <p className="text-sm font-medium">Thanks! We'll notify you soon.</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Contact Support Section */}
            <div className="slide-in-up">
              <div className="rounded-xl bg-gradient-to-br from-base-100 to-base-200 p-6 shadow-md dark:from-slate-800 dark:to-slate-700 sm:p-8">
                <h3 className="mb-4 text-lg font-bold text-base-content dark:text-white">
                  Need Help?
                </h3>
                <p className="mb-6 text-sm text-base-content/70 dark:text-slate-400">
                  If you need immediate assistance, please contact our support
                  team
                </p>

                <div className="grid gap-3 sm:grid-cols-3">
                  {/* Email Support */}
                  <a
                    className="flex items-center gap-3 rounded-lg bg-base-100 p-4 transition-all duration-300 hover:bg-primary/10 dark:bg-slate-700 dark:hover:bg-slate-600 sm:flex-col sm:text-center"
                    href={`mailto:${contactInfo?.email || "support@hareeng.org"}`}
                  >
                    <Mail
                      className="dark:text-primary-light text-primary"
                      size={20}
                    />
                    <div className="flex-1 sm:flex-none">
                      <p className="text-xs font-semibold text-base-content/60 dark:text-slate-400">
                        Email
                      </p>
                      <p className="text-sm font-bold text-base-content dark:text-white">
                        {contactInfo?.email || "support@hareeng.org"}
                      </p>
                    </div>
                  </a>

                  {/* Phone Support */}
                  <a
                    className="flex items-center gap-3 rounded-lg bg-base-100 p-4 transition-all duration-300 hover:bg-primary/10 dark:bg-slate-700 dark:hover:bg-slate-600 sm:flex-col sm:text-center"
                    href={`tel:${(contactInfo?.phone || "+266 5048 0814").replace(/\s/g, "")}`}
                  >
                    <Phone
                      className="dark:text-primary-light text-primary"
                      size={20}
                    />
                    <div className="flex-1 sm:flex-none">
                      <p className="text-xs font-semibold text-base-content/60 dark:text-slate-400">
                        Phone
                      </p>
                      <p className="text-sm font-bold text-base-content dark:text-white">
                        {contactInfo?.phone || "+266 5048 0814"}
                      </p>
                    </div>
                  </a>

                  {/* Live Chat */}
                  <button className="flex items-center gap-3 rounded-lg bg-base-100 p-4 transition-all duration-300 hover:bg-primary/10 dark:bg-slate-700 dark:hover:bg-slate-600 sm:flex-col sm:text-center">
                    <MessageSquare
                      className="dark:text-primary-light text-primary"
                      size={20}
                    />
                    <div>
                      <p className="text-xs font-semibold text-base-content/60 dark:text-slate-400">
                        Chat
                      </p>
                      <p className="text-sm font-bold text-base-content dark:text-white">
                        Live Chat
                      </p>
                    </div>
                  </button>
                </div>
              </div>
            </div>

            {/* Return to Home Button */}
            <div className="mt-12 text-center">
              <button
                className="btn btn-outline gap-2"
                onClick={() => navigate("/")}
              >
                Return to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
